import type {
  PunchApp,
  PunchHistoryEntry,
  PunchItem,
  PunchItemInput,
  PunchListView,
  PunchStatus,
} from "@fieldpath/shared";
import { PUNCH_FOR_REVIEW, PUNCH_NOT_LOOKED_AT, mdyTime } from "@fieldpath/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError, api } from "../api/client";
import { useRefreshOnReturn } from "../refreshOnReturn";
import type { Fetcher } from "../api/client";
import { punchCsv } from "../punchCsv";
import { navigate } from "../router";
import { HelpButton } from "./HelpButton";
import { RichText } from "./RichText";

/**
 * THE PUNCH LIST (Jeremy, 27 Oct 2026): every problem report from Fieldpath and
 * the resource scheduler, triaged in one list that reads like the two
 * spreadsheets it replaced -- the same columns, in the same order -- with no
 * version files: each change is kept in the row's history instead.
 *
 * A face, no rules. Who may see it (punchlist.members), who writes Jeremy's
 * notes (punchlist.deciders), what a status may be: all the API's
 * (api/src/db/punch.ts). This decides only what to show and in what order.
 *
 * Laid out like the project Grid: a list on the left, the selected row's editor
 * on the right, stacked when the card is narrow. The row's address is
 * /punchlist/F94, so a row can be pasted to somebody.
 */

type AppFilter = "all" | PunchApp;
/**
 * "todo" is everything whose status is not of kind `closed` -- since 30 Oct
 * (F165) the two Promoted statuses are among those, so Still to do is the
 * default and the list that needs work.
 */
type StatusFilter = "todo" | "all" | string;
/** "any", one priority ("1".."5"), or "none" for a row nobody has given one. */
type PriorityFilter = "any" | "none" | string;

/** The priorities, 1 the highest (F152). */
const PRIORITIES = [1, 2, 3, 4, 5] as const;

function priorityLabel(p: number): string {
  return p === 1 ? "1 - highest" : p === 5 ? "5 - lowest" : String(p);
}

const APP_NAME: Record<PunchApp, string> = { fieldpath: "Fieldpath", scheduler: "Scheduler" };

function download(items: readonly PunchItem[]): void {
  const blob = new Blob([punchCsv(items)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `punch-list-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Absolute, in the reader's timezone, as everywhere on the admin side. */
function stamp(iso: string): string {
  return mdyTime(iso);
}

function firstLine(text: string): string {
  return text.trim().split("\n")[0]!.trim();
}

/** Only an http(s) address becomes a link, as in RichText. */
function isLink(url: string | null): url is string {
  return url !== null && /^https?:\/\//i.test(url);
}

/** Where the report a row came from can be read: its line on the admin page, opened. */
function reportHref(item: PunchItem): string | null {
  if (!item.sourceReportId) return null;
  const anchor = item.app === "fieldpath" ? "report" : "scheduler-report";
  return `/admin#${anchor}-${encodeURIComponent(item.sourceReportId)}`;
}

/** The chip's class: "not yet looked at" must look like nothing else on the page. */
function statusClass(status: string, kinds: ReadonlyMap<string, PunchStatus["kind"]>): string {
  if (status === PUNCH_NOT_LOOKED_AT) return "punch-status unseen";
  if (status === PUNCH_FOR_REVIEW) return "punch-status review";
  // Before the kind: the Promoted statuses are `closed` since F165 and still read as shipped.
  if (status.startsWith("Fixed on dev") || status.startsWith("Promoted")) return "punch-status shipped";
  const kind = kinds.get(status);
  if (kind === "closed") return "punch-status closed";
  if (kind === "broader") return "punch-status broader";
  return "punch-status";
}

export function PunchList({ fetcher, label }: { fetcher: Fetcher; label: string | null }) {
  const [view, setView] = useState<PunchListView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);
  const [app, setApp] = useState<AppFilter>("all");
  const [status, setStatus] = useState<StatusFilter>("todo");
  const [priority, setPriority] = useState<PriorityFilter>("any");
  const [query, setQuery] = useState("");
  const [descending, setDescending] = useState(false);
  const [byPriority, setByPriority] = useState(false);
  const [adding, setAdding] = useState(false);
  const [showHow, setShowHow] = useState(false);
  const editorRef = useRef<HTMLElement>(null);
  // Whether the open row has unsaved edits, so moving off it asks first.
  const dirtyRef = useRef(false);

  const load = useCallback(async () => {
    try {
      setView(await api.punchList(fetcher));
      setError(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) setDenied(true);
      else setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [fetcher]);

  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  useEffect(() => {
    void load();
  }, [load, back]);

  const kinds = useMemo(
    () => new Map((view?.statuses ?? []).map((s) => [s.name, s.kind] as const)),
    [view],
  );

  const shown = useMemo(() => {
    if (!view) return [];
    const q = query.trim().toLowerCase();
    const out = view.items.filter((i) => {
      if (app !== "all" && i.app !== app) return false;
      if (status === "todo" && kinds.get(i.status) === "closed") return false;
      if (status !== "todo" && status !== "all" && i.status !== status) return false;
      if (priority === "none" && i.priority !== null) return false;
      if (priority !== "any" && priority !== "none" && i.priority !== Number(priority)) return false;
      if (!q) return true;
      return [i.label, i.screen, i.concern, i.otherNotes, i.reportedBy, i.type, i.status, i.claudeResponse, i.prCommit, i.jeremyNotes]
        .some((v) => v?.toLowerCase().includes(q));
    });
    // By number within each app, Fieldpath first: F1..F94, then S1..S10. By
    // priority, 1 first and a row with none LAST, the number order within each.
    const order = (i: PunchItem) => (i.app === "fieldpath" ? 0 : 1);
    const rank = (i: PunchItem) => i.priority ?? 6;
    out.sort(
      (a, b) =>
        (byPriority ? rank(a) - rank(b) : 0) ||
        order(a) - order(b) ||
        (descending ? b.number - a.number : a.number - b.number),
    );
    return out;
  }, [view, app, status, priority, query, descending, byPriority, kinds]);

  const count = (name: string) =>
    view?.items.filter((i) => i.status === name && (app === "all" || i.app === app)).length ?? 0;

  const selected = view && label ? (view.items.find((i) => i.label === label) ?? null) : null;

  const select = (item: PunchItem | null) => {
    if (dirtyRef.current && !window.confirm("Leave this row without saving your changes?")) return;
    dirtyRef.current = false;
    setAdding(false);
    navigate({ kind: "punchlist", label: item?.label ?? null }, true);
  };

  // A row opened from a pasted address: show it in the list, once, when the list arrives.
  const scrolledTo = useRef(false);
  useEffect(() => {
    if (!view || !label || scrolledTo.current) return;
    scrolledTo.current = true;
    // Stacked, the open row is above the list, and the effect below shows it instead.
    if (window.matchMedia("(max-width: 900px)").matches) return;
    document.getElementById(`punch-row-${label}`)?.scrollIntoView({ block: "center" });
  }, [view, label]);

  // A row picked on a phone: bring the editor, stacked below the list, into view.
  useEffect(() => {
    if ((selected || adding) && window.matchMedia("(max-width: 900px)").matches) {
      editorRef.current?.scrollIntoView({ block: "start" });
    }
  }, [selected, adding]);

  if (denied) {
    return (
      <div className="card">
        <h1>Punch list</h1>
        <p>The punch list is for the people on its members setting (Admin, Settings).</p>
      </div>
    );
  }
  if (error && !view) return <div className="error">{error}</div>;
  if (!view) return <p className="muted">Loading the punch list…</p>;

  const replaced = (item: PunchItem) =>
    setView((v) =>
      v
        ? {
            ...v,
            items: v.items.some((i) => i.id === item.id)
              ? v.items.map((i) => (i.id === item.id ? item : i))
              : [...v.items, item],
          }
        : v,
    );

  return (
    <>
      <div className="punch-head">
        <h1>
          Punch list
          <HelpButton screens="punchlist" />
        </h1>
        <div className="punch-actions">
          <button
            onClick={() => {
              navigate({ kind: "punchlist", label: null }, true);
              setAdding(true);
            }}
          >
            Add a row
          </button>
          <button onClick={() => download(shown)} title="The rows shown now, with the spreadsheet's columns">
            Download for Excel ({shown.length})
          </button>
        </div>
      </div>
      <p className="muted punch-lede">
        Every problem report from Fieldpath and the resource scheduler arrives here by itself as{" "}
        <strong>{PUNCH_NOT_LOOKED_AT}</strong>. Claude writes the response, Jeremy decides in his notes,
        and the status follows the fix to production.{" "}
        <button className="linkish" onClick={() => setShowHow((v) => !v)} aria-expanded={showHow}>
          {showHow ? "Hide the statuses" : "What each status means"}
        </button>
      </p>
      {showHow ? <HowItWorks view={view} /> : null}

      {!view.schedulerIntake.available ? (
        <p className="muted admin-warn punch-warn">
          <strong>The scheduler's reports could not be read just now.</strong> {view.schedulerIntake.reason}{" "}
          Any reported there since the last read are not on this list yet; they arrive at the next read.
        </p>
      ) : null}

      <div className="punch-filters">
        <div className="view-switch" role="group" aria-label="Which app">
          {(["all", "fieldpath", "scheduler"] as const).map((a) => (
            <button key={a} className={app === a ? "on" : ""} onClick={() => setApp(a)}>
              {a === "all" ? "Both apps" : APP_NAME[a]}
            </button>
          ))}
        </div>
        <select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)} className="punch-status-filter">
          <option value="todo">Still to do</option>
          <option value="all">Every status</option>
          {view.statuses.map((s) => (
            <option key={s.name} value={s.name}>
              {s.name} ({count(s.name)})
            </option>
          ))}
        </select>
        <select
          aria-label="Priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          className="punch-priority-filter"
        >
          <option value="any">Any priority</option>
          {PRIORITIES.map((p) => (
            <option key={p} value={String(p)}>
              Priority {priorityLabel(p)}
            </option>
          ))}
          <option value="none">No priority</option>
        </select>
        <input
          type="search"
          className="punch-search"
          placeholder="Search every column"
          aria-label="Search the punch list"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <div className="punch-quick" role="group" aria-label="Quick filters">
        {[PUNCH_NOT_LOOKED_AT, PUNCH_FOR_REVIEW].map((s) => (
          <button
            key={s}
            className={`punch-quick-btn${s === PUNCH_NOT_LOOKED_AT ? " unseen" : ""}${status === s ? " on" : ""}`}
            onClick={() => setStatus(status === s ? "todo" : s)}
            aria-pressed={status === s}
          >
            {s} <span className="punch-quick-n">{count(s)}</span>
          </button>
        ))}
        <span className="muted punch-shown">
          {shown.length} of {view.items.filter((i) => app === "all" || i.app === app).length} shown
        </span>
      </div>

      {error ? <p className="error-inline">{error}</p> : null}

      <div className="punch-card">
        <div className={`punch-body${selected || adding ? " with-editor" : ""}`}>
          <div className="punch-roster">
            <div className="punch-row punch-row-head" aria-hidden="true">
              <button
                className={`punch-sort${byPriority ? " off" : ""}`}
                onClick={() => (byPriority ? setByPriority(false) : setDescending((v) => !v))}
                tabIndex={-1}
              >
                # {byPriority ? "" : descending ? "↓" : "↑"}
              </button>
              <span>Screen · Concern</span>
              <button
                className={`punch-sort punch-col-pri${byPriority ? "" : " off"}`}
                onClick={() => setByPriority((v) => !v)}
                tabIndex={-1}
                title="Sort by priority, 1 first, none last"
              >
                Pri {byPriority ? "↑" : ""}
              </button>
              <span className="punch-col-type">Type</span>
              <span className="punch-col-status">Status</span>
              <span className="punch-col-pr">PR / commit</span>
            </div>
            {shown.length === 0 ? (
              <p className="muted punch-empty">Nothing matches. Try Every status, or clear the search.</p>
            ) : (
              shown.map((item) => (
                <button
                  key={item.id}
                  id={`punch-row-${item.label}`}
                  className={[
                    "punch-row",
                    item.status === PUNCH_NOT_LOOKED_AT ? "unseen" : "",
                    selected?.id === item.id ? "selected" : "",
                  ].join(" ")}
                  onClick={() => select(selected?.id === item.id ? null : item)}
                  aria-pressed={selected?.id === item.id}
                >
                  <span className="punch-num">{item.label}</span>
                  <span className="punch-what">
                    <span className="punch-concern">{firstLine(item.concern)}</span>
                    <span className="punch-screen">
                      {item.screen ? firstLine(item.screen) : "No screen given"}
                      {item.reportedBy ? ` · ${item.reportedBy}` : ""}
                    </span>
                  </span>
                  <span className="punch-col-pri">
                    {item.priority !== null ? (
                      <span className={`punch-pri p${item.priority}`} title={`Priority ${priorityLabel(item.priority)}`}>
                        P{item.priority}
                      </span>
                    ) : (
                      <span className="punch-pri-none">—</span>
                    )}
                  </span>
                  <span className="punch-col-type punch-type">{item.type ?? "—"}</span>
                  <span className="punch-col-status">
                    <span className={statusClass(item.status, kinds)}>{item.status}</span>
                  </span>
                  <span className="punch-col-pr punch-pr">{item.prCommit ?? ""}</span>
                </button>
              ))
            )}
          </div>
          {adding ? (
            <aside className="punch-editor" ref={editorRef} aria-label="A new row">
              <NewRow
                fetcher={fetcher}
                view={view}
                defaultApp={app === "all" ? "fieldpath" : app}
                onCancel={() => setAdding(false)}
                onAdded={(item) => {
                  replaced(item);
                  setAdding(false);
                  navigate({ kind: "punchlist", label: item.label }, true);
                }}
              />
            </aside>
          ) : selected ? (
            <aside className="punch-editor" ref={editorRef} aria-label={`Editing ${selected.label}`}>
              <Editor
                key={selected.id}
                fetcher={fetcher}
                item={selected}
                view={view}
                kinds={kinds}
                onSaved={replaced}
                onDirty={(d) => {
                  dirtyRef.current = d;
                }}
                onClose={() => select(null)}
              />
            </aside>
          ) : null}
        </div>
      </div>
    </>
  );
}

/** The statuses and types with their meanings, from the database, and the order a row moves through. */
function HowItWorks({ view }: { view: PunchListView }) {
  return (
    <div className="card punch-how">
      <h2>How a report moves through the list</h2>
      <ol>
        <li>A report arrives, in either app, as <strong>{PUNCH_NOT_LOOKED_AT}</strong>.</li>
        <li>
          Claude investigates, writes the Claude response, and moves it to <strong>{PUNCH_FOR_REVIEW}</strong>.
          A company-wide question goes there too, marked as one in the response, with no recommendation.
        </li>
        <li>Jeremy decides in Jeremy's notes / decision.</li>
        <li>
          Claude builds it: <strong>Fixed on dev</strong>, then <strong>Promoted - confirm on erpapp</strong> or{" "}
          <strong>erpsch</strong>, the end of the line. Promoted counts as done: Still to do leaves it out, with
          Closed (as built) and Canceled.
        </li>
      </ol>
      <h2>Statuses</h2>
      <dl className="punch-defs">
        {view.statuses.map((s) => (
          <div key={s.name}>
            <dt>{s.name}</dt>
            <dd>{s.meaning}</dd>
          </div>
        ))}
      </dl>
      <h2>Types</h2>
      <dl className="punch-defs">
        {view.types.map((t) => (
          <div key={t.name}>
            <dt>{t.name}</dt>
            <dd>{t.meaning}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** The editable text of a row, as the inputs hold it: "" for nothing. */
type Draft = Record<
  "screen" | "concern" | "otherNotes" | "exampleUrl" | "reportedBy" | "type" | "claudeResponse" | "status" | "prCommit" | "priority" | "jeremyNotes",
  string
>;

function draftOf(item: PunchItem): Draft {
  return {
    screen: item.screen ?? "",
    concern: item.concern,
    otherNotes: item.otherNotes ?? "",
    exampleUrl: item.exampleUrl ?? "",
    reportedBy: item.reportedBy ?? "",
    type: item.type ?? "",
    claudeResponse: item.claudeResponse ?? "",
    status: item.status,
    prCommit: item.prCommit ?? "",
    priority: item.priority === null ? "" : String(item.priority),
    jeremyNotes: item.jeremyNotes ?? "",
  };
}

/** What changed, as the API takes it: only those fields, blank as null, priority a number. */
function changes(before: Draft, after: Draft): PunchItemInput {
  const out: Record<string, string | number | null> = {};
  for (const key of Object.keys(after) as (keyof Draft)[]) {
    if (after[key] === before[key]) continue;
    if (key === "priority") out[key] = after[key] === "" ? null : Number(after[key]);
    else out[key] = key === "concern" || key === "status" ? after[key] : after[key].trim() === "" ? null : after[key];
  }
  return out as PunchItemInput;
}

/** The statuses there are. Saving never adds one (F165, 30 Oct 2026). */
function StatusPicker({
  view,
  value,
  onChange,
  id,
}: {
  view: PunchListView;
  value: string;
  onChange: (v: string) => void;
  id: string;
}) {
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      {view.statuses.map((s) => (
        <option key={s.name} value={s.name}>
          {s.name}
        </option>
      ))}
    </select>
  );
}

/** 1 to 5, 1 the highest, or Not set (F152). */
function PriorityPicker({ value, onChange, id }: { value: string; onChange: (v: string) => void; id: string }) {
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Not set</option>
      {PRIORITIES.map((p) => (
        <option key={p} value={String(p)}>
          {priorityLabel(p)}
        </option>
      ))}
    </select>
  );
}

/** What the chosen status means, under the Type and Status pair, at the editor's full width. */
function StatusMeaning({ view, status }: { view: PunchListView; status: string }) {
  const meaning = view.statuses.find((s) => s.name === status)?.meaning;
  return meaning ? <p className="punch-meaning punch-status-meaning">{meaning}</p> : null;
}

function Editor({
  fetcher,
  item,
  view,
  kinds,
  onSaved,
  onDirty,
  onClose,
}: {
  fetcher: Fetcher;
  item: PunchItem;
  view: PunchListView;
  kinds: ReadonlyMap<string, PunchStatus["kind"]>;
  onSaved: (item: PunchItem) => void;
  onDirty: (dirty: boolean) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftOf(item));
  const [editingResponse, setEditingResponse] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<PunchHistoryEntry[] | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const base = draftOf(item);
  const patch = changes(base, draft);
  const dirty = Object.keys(patch).length > 0;
  useEffect(() => onDirty(dirty), [dirty, onDirty]);
  const set = (key: keyof Draft) => (e: { target: { value: string } }) =>
    setDraft((d) => ({ ...d, [key]: e.target.value }));

  useEffect(() => {
    if (!showHistory) return;
    void api.punchHistory(fetcher, item.id).then(setHistory).catch(() => setHistory([]));
  }, [fetcher, item.id, item.updatedAt, showHistory]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const saved = await api.updatePunchItem(fetcher, item.id, patch);
      onSaved(saved);
      setDraft(draftOf(saved));
      setEditingResponse(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const href = reportHref(item);
  const id = (f: string) => `punch-${item.id}-${f}`;

  return (
    <>
      <div className="punch-stepper">
        <span>
          {item.label} · {APP_NAME[item.app]}
        </span>
        <button onClick={onClose} aria-label="Close the row">
          Close ×
        </button>
      </div>
      <div>
        <span className={statusClass(item.status, kinds)}>{item.status}</span>
      </div>

      <div>
        <label className="grid-label" htmlFor={id("concern")}>Concern</label>
        <textarea id={id("concern")} value={draft.concern} onChange={set("concern")} rows={4} />
      </div>

      <div className="punch-pair">
        <div>
          <label className="grid-label" htmlFor={id("screen")}>Screen</label>
          <input id={id("screen")} value={draft.screen} onChange={set("screen")} />
        </div>
        <div>
          <label className="grid-label" htmlFor={id("reported")}>Reported by</label>
          <input id={id("reported")} value={draft.reportedBy} onChange={set("reportedBy")} />
        </div>
      </div>

      <div className="punch-pair">
        <div>
          <label className="grid-label" htmlFor={id("type")}>Type</label>
          <select id={id("type")} value={draft.type} onChange={set("type")}>
            <option value="">Not set</option>
            {view.types.map((t) => (
              <option key={t.name} value={t.name}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="grid-label" htmlFor={id("status")}>Status</label>
          <StatusPicker view={view} id={id("status")} value={draft.status} onChange={(v) => setDraft((d) => ({ ...d, status: v }))} />
        </div>
      </div>
      <StatusMeaning view={view} status={draft.status} />

      <div>
        <span className="grid-label">Claude response</span>
        {editingResponse ? (
          <>
            <textarea
              aria-label="Claude response"
              value={draft.claudeResponse}
              onChange={set("claudeResponse")}
              rows={10}
            />
            <p className="punch-meaning">
              Bullets start with "- ", **bold**, and http links become links.
            </p>
          </>
        ) : (
          <div className="punch-response">
            {draft.claudeResponse.trim() ? (
              <RichText text={draft.claudeResponse} />
            ) : (
              <p className="muted">Nothing written yet.</p>
            )}
            <button className="linkish punch-edit-response" onClick={() => setEditingResponse(true)}>
              Edit
            </button>
          </div>
        )}
      </div>

      <div>
        <label className="grid-label" htmlFor={id("notes")}>
          Jeremy's notes / decision{view.decider ? "" : " — Jeremy's alone"}
        </label>
        {view.decider ? (
          <textarea id={id("notes")} value={draft.jeremyNotes} onChange={set("jeremyNotes")} rows={3} />
        ) : (
          <p className="punch-readonly" id={id("notes")}>
            {item.jeremyNotes ?? <span className="muted">No decision written.</span>}
          </p>
        )}
      </div>

      <div className="punch-pair">
        <div>
          <label className="grid-label" htmlFor={id("priority")}>Priority</label>
          <PriorityPicker id={id("priority")} value={draft.priority} onChange={(v) => setDraft((d) => ({ ...d, priority: v }))} />
        </div>
        <div>
          <label className="grid-label" htmlFor={id("pr")}>PR / commit</label>
          <input id={id("pr")} value={draft.prCommit} onChange={set("prCommit")} />
        </div>
      </div>

      <div>
        <label className="grid-label" htmlFor={id("url")}>
          Example URL
          {isLink(draft.exampleUrl.trim() || null) ? (
            <>
              {" · "}
              <a href={draft.exampleUrl.trim()} target="_blank" rel="noopener noreferrer">
                open ↗
              </a>
            </>
          ) : null}
        </label>
        <input id={id("url")} value={draft.exampleUrl} onChange={set("exampleUrl")} />
      </div>

      <div>
        <label className="grid-label" htmlFor={id("other")}>Other notes</label>
        <textarea id={id("other")} value={draft.otherNotes} onChange={set("otherNotes")} rows={3} />
      </div>

      <p className="punch-source">
        {href ? (
          <a href={href} target="_blank" rel="noopener noreferrer">
            The report it came from ↗
          </a>
        ) : (
          <span className="muted">No report linked: added by hand, or from a spreadsheet row no report could be matched to.</span>
        )}
      </p>

      {error ? <p className="error-inline">Not saved: {error}</p> : null}
      <div className="punch-save">
        <button className="primary" disabled={!dirty || busy} onClick={() => void save()}>
          {busy ? "Saving…" : "Save"}
        </button>
        <button disabled={!dirty || busy} onClick={() => setDraft(base)}>
          Discard
        </button>
        <span className="muted punch-updated">
          Updated {stamp(item.updatedAt)}
          {item.updatedBy ? ` by ${item.updatedBy}` : ""}
        </span>
      </div>

      <div>
        <button className="linkish expander" onClick={() => setShowHistory((v) => !v)} aria-expanded={showHistory}>
          {showHistory ? "Hide the history" : "Show the history"}
        </button>
        {showHistory ? (
          history === null ? (
            <p className="muted">Loading…</p>
          ) : (
            <ol className="punch-history">
              {history.map((h, i) => (
                <li key={i}>
                  <span className="punch-history-when">
                    {stamp(h.changedAt)} · {h.changedBy}
                    {/* Straight in SQL: said, so it never reads as a change made on this page. */}
                    {h.viaSql ? (h.changedBy === h.dbRole ? ", directly in the database" : `, via ${h.dbRole}`) : ""}
                  </span>
                  <span>
                    {h.field === "created" ? (
                      <>Added, as {h.newValue}</>
                    ) : (
                      <>
                        <strong>{h.field.replace(/_/g, " ")}</strong>: {short(h.oldValue)} → {short(h.newValue)}
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          )
        ) : null}
      </div>
    </>
  );
}

function short(value: string | null): string {
  if (value === null || value === "") return "(blank)";
  const one = value.replace(/\s+/g, " ").trim();
  return one.length > 140 ? `${one.slice(0, 140)}…` : one;
}

function NewRow({
  fetcher,
  view,
  defaultApp,
  onCancel,
  onAdded,
}: {
  fetcher: Fetcher;
  view: PunchListView;
  defaultApp: PunchApp;
  onCancel: () => void;
  onAdded: (item: PunchItem) => void;
}) {
  const [app, setApp] = useState<PunchApp>(defaultApp);
  const [concern, setConcern] = useState("");
  const [screen, setScreen] = useState("");
  const [reportedBy, setReportedBy] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState(PUNCH_NOT_LOOKED_AT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      onAdded(
        await api.createPunchItem(fetcher, {
          app,
          concern,
          screen: screen.trim() || null,
          reportedBy: reportedBy.trim() || null,
          type: type || null,
          status,
        }),
      );
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="punch-stepper">
        <span>A new row</span>
        <button onClick={onCancel}>Cancel ×</button>
      </div>
      <p className="punch-meaning">
        For something nobody filed through Report a problem. It takes the next number for its app.
      </p>
      <div className="view-switch" role="group" aria-label="Which app">
        {(["fieldpath", "scheduler"] as const).map((a) => (
          <button key={a} className={app === a ? "on" : ""} onClick={() => setApp(a)}>
            {APP_NAME[a]}
          </button>
        ))}
      </div>
      <div>
        <label className="grid-label" htmlFor="punch-new-concern">Concern</label>
        <textarea id="punch-new-concern" value={concern} onChange={(e) => setConcern(e.target.value)} rows={4} />
      </div>
      <div className="punch-pair">
        <div>
          <label className="grid-label" htmlFor="punch-new-screen">Screen</label>
          <input id="punch-new-screen" value={screen} onChange={(e) => setScreen(e.target.value)} />
        </div>
        <div>
          <label className="grid-label" htmlFor="punch-new-reported">Reported by</label>
          <input id="punch-new-reported" value={reportedBy} onChange={(e) => setReportedBy(e.target.value)} />
        </div>
      </div>
      <div className="punch-pair">
        <div>
          <label className="grid-label" htmlFor="punch-new-type">Type</label>
          <select id="punch-new-type" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">Not set</option>
            {view.types.map((t) => (
              <option key={t.name} value={t.name}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="grid-label" htmlFor="punch-new-status">Status</label>
          <StatusPicker view={view} id="punch-new-status" value={status} onChange={setStatus} />
        </div>
      </div>
      <StatusMeaning view={view} status={status} />
      {error ? <p className="error-inline">Not added: {error}</p> : null}
      <div className="punch-save">
        <button className="primary" disabled={busy || concern.trim() === ""} onClick={() => void add()}>
          {busy ? "Adding…" : "Add the row"}
        </button>
      </div>
    </>
  );
}
